// Character Data Service
// Handles transformations between DB Character, API Character, and Game Character
// MapleStory-inspired architecture for clean data flow

import { createHelix2000Character } from '../../shared/CharacterClasses';
import type { EquipmentSchemaSlot } from '../../shared/equipmentSlots';
import { CharacterClass, Helix2000Character, QuestLog } from '../../shared/CharacterTypes';
import { WORLD_COORDINATE_ENVELOPE_PX } from '../../shared/WorldCoordinates';
import { GAME_CONSTANTS } from '../constants/GameConstants';

// Database/API Character Interface
export interface DBCharacter {
  _id: string;
  name: string;
  class: string;
  job?: string;
  level: number;
  xp: number;
  x: number;
  y: number;
  health?: number;
  mana?: number;
  maxHealth?: number;
  maxMana?: number;
  gold?: number;
  inventory?: any[];
  /** Authoritative equipped item IDs, separate from inventory and cosmetics. */
  equipment?: Partial<Record<EquipmentSchemaSlot, string>>;
  quests?: any;
  completedQuests?: string[];
  appearance?: any;
  stats?: any;
  strength?: number;
  dexterity?: number;
  intelligence?: number;
  luck?: number;
  statPoints?: number;
  attack?: number;
  magicAttack?: number;
  defense?: number;
  magicDefense?: number;
  accuracy?: number;
  avoidability?: number;
  criticalRate?: number;
  speed?: number;
  jump?: number;
  currentLevelId?: string;
  /** Server-authored initial/saved map on the character API record. */
  levelId?: string;
  userId?: string;
  created?: Date;
  lastPlayed?: Date;
  pin?: string;
  hotbar?: {
    slots: any; // Record or Map
    activeBar: number;
  };
}

export class CharacterService {
  private static instance: CharacterService;

  private constructor() { }

  public static getInstance(): CharacterService {
    if (!CharacterService.instance) {
      CharacterService.instance = new CharacterService();
    }
    return CharacterService.instance;
  }

  /**
   * Converts database character to game-ready character.
   * Validates data and provides sensible defaults.
   * @param dbCharacter The character data from the database.
   * @returns A fully initialized Helix2000Character ready for the game engine.
   * @throws Error if required fields (id, name, class) are missing.
   */
  public convertDBToGameCharacter(dbCharacter: DBCharacter): Helix2000Character {
    // Validate required fields
    if (!dbCharacter._id || !dbCharacter.name || !dbCharacter.class) {
      throw new Error(`Invalid character data: Missing required fields for ${dbCharacter._id}`);
    }

    // Create base character using factory
    const gameChar = createHelix2000Character(
      dbCharacter.name,
      dbCharacter.class as CharacterClass
    );

    // Apply job if present, otherwise factory handles default
    if (dbCharacter.job) {
      gameChar.job = dbCharacter.job as any;
    }

    // Apply database ID
    gameChar._id = dbCharacter._id;
    gameChar.id = dbCharacter._id; // Unified ID access

    // Apply stats with validation
    gameChar.level = this.validateNumber(dbCharacter.level, 1, 1, 999);
    gameChar.xp = this.validateNumber(dbCharacter.xp, 0, 0, Infinity);

    // Preserve production-scale persisted positions. MapSystem validates the
    // position against the active level when spawning; this broad clamp only
    // keeps pathological persistence values out of the client model.
    gameChar.position.x = this.validateNumber(
      dbCharacter.x,
      200,
      -WORLD_COORDINATE_ENVELOPE_PX,
      WORLD_COORDINATE_ENVELOPE_PX,
    );
    gameChar.position.y = this.validateNumber(
      dbCharacter.y,
      300,
      -WORLD_COORDINATE_ENVELOPE_PX,
      WORLD_COORDINATE_ENVELOPE_PX,
    );

    // Health/Mana with fallback to defaults
    if (dbCharacter.health != null) gameChar.health = dbCharacter.health;
    if (dbCharacter.maxHealth != null) gameChar.maxHealth = dbCharacter.maxHealth;
    if (dbCharacter.mana != null) gameChar.mana = dbCharacter.mana;
    if (dbCharacter.maxMana != null) gameChar.maxMana = dbCharacter.maxMana;

    // Gold (default if not provided)
    gameChar.inventory.gold = this.validateNumber(dbCharacter.gold, 0, 0, 9999999);

    // Inventory items
    if (dbCharacter.inventory && Array.isArray(dbCharacter.inventory)) {
      dbCharacter.inventory.forEach((item: any) => {
        if (item && item.id) {
          gameChar.inventory.items.push(item);
        }
      });
    }

    // Quests conversion
    if (dbCharacter.quests || dbCharacter.completedQuests) {
      gameChar.quests = this.convertQuests(dbCharacter.quests, dbCharacter.completedQuests);
    }

    // Appearance
    if (dbCharacter.appearance) {
      gameChar.appearance = dbCharacter.appearance;
    }

    // Stats - Resilient mapping (handle nested or top-level)
    const stats = gameChar.stats;

    // Check top-level DB fields first (Standard for our CharacterModel)
    if (dbCharacter.strength !== undefined) stats.strength = dbCharacter.strength;
    if (dbCharacter.dexterity !== undefined) stats.dexterity = dbCharacter.dexterity;
    if (dbCharacter.intelligence !== undefined) stats.intelligence = dbCharacter.intelligence;
    if (dbCharacter.luck !== undefined) stats.luck = dbCharacter.luck;

    // Secondary Stats
    if (dbCharacter.attack !== undefined) stats.attack = dbCharacter.attack;
    if (dbCharacter.magicAttack !== undefined) stats.magicAttack = dbCharacter.magicAttack;
    if (dbCharacter.defense !== undefined) stats.defense = dbCharacter.defense;
    if (dbCharacter.magicDefense !== undefined) stats.magicDefense = dbCharacter.magicDefense;
    if (dbCharacter.accuracy !== undefined) stats.accuracy = dbCharacter.accuracy;
    if (dbCharacter.avoidability !== undefined) stats.avoidability = dbCharacter.avoidability;
    if (dbCharacter.criticalRate !== undefined) stats.criticalRate = dbCharacter.criticalRate;
    if (dbCharacter.speed !== undefined) stats.speed = dbCharacter.speed;
    if (dbCharacter.jump !== undefined) stats.jump = dbCharacter.jump;

    // Finally, override with nested stats if they exist (backward compatibility)
    if (dbCharacter.stats) {
      gameChar.stats = { ...gameChar.stats, ...dbCharacter.stats };
    }

    // Leveling Points
    gameChar.statPoints = this.validateNumber(dbCharacter.statPoints, 0, 0, 999);

    // Hotbar
    if (dbCharacter.hotbar) {
      gameChar.actionBar = {
        slots: Array.isArray(dbCharacter.hotbar.slots)
          ? dbCharacter.hotbar.slots
          : Object.values(dbCharacter.hotbar.slots || {}),
        activeBar: dbCharacter.hotbar.activeBar || 1
      };
    }

    return gameChar;
  }

  /**
   * Convert character for saving back to database.
   * Serializes complex objects like Quests into storage-friendly formats.
   * @param gameChar The runtime game character to save.
   * @returns A DBCharacter object ready for database insertion/update.
   */
  public convertGameToDBCharacter(gameChar: Helix2000Character): DBCharacter {
    const dbChar: DBCharacter = {
      _id: gameChar._id,
      name: gameChar.name,
      class: gameChar.class,
      job: gameChar.job,
      level: gameChar.level,
      xp: gameChar.xp,
      x: gameChar.position.x,
      y: gameChar.position.y,
      health: gameChar.health,
      mana: gameChar.mana,
      maxHealth: gameChar.maxHealth,
      maxMana: gameChar.maxMana,
      gold: gameChar.inventory.gold,
      inventory: gameChar.inventory.items,
      appearance: gameChar.appearance,
      stats: gameChar.stats,
      // Map primary stats to top-level for CharacterModel consistency
      strength: gameChar.stats.strength,
      dexterity: gameChar.stats.dexterity,
      intelligence: gameChar.stats.intelligence,
      luck: gameChar.stats.luck,
      statPoints: gameChar.statPoints,
      // Secondary stats
      attack: gameChar.stats.attack,
      magicAttack: gameChar.stats.magicAttack,
      defense: gameChar.stats.defense,
      magicDefense: gameChar.stats.magicDefense,
      accuracy: gameChar.stats.accuracy,
      avoidability: gameChar.stats.avoidability,
      criticalRate: gameChar.stats.criticalRate,
      speed: gameChar.stats.speed,
      jump: gameChar.stats.jump,
      currentLevelId: (gameChar as any).currentLevelId
    };

    // Convert quests to storable format
    dbChar.quests = this.serializeQuests(gameChar.quests);
    dbChar.completedQuests = [...(gameChar.quests?.completed || [])];

    // Save hotbar
    if (gameChar.actionBar) {
      dbChar.hotbar = {
        slots: gameChar.actionBar.slots,
        activeBar: gameChar.actionBar.activeBar
      };
    }

    return dbChar;
  }

  /**
   * Create a new character with defaults.
   * Generates a unique ID and sets initial position/stats.
   * @param name The display name for the new character.
   * @param characterClass The class/job of the character.
   * @param userId Optional user ID to bind the character to.
   * @returns A new Helix2000Character instance.
   */
  public createNewCharacter(
    name: string,
    characterClass: CharacterClass,
    userId?: string
  ): Helix2000Character {
    const gameChar = createHelix2000Character(name, characterClass);

    // Generate unique ID
    const timestamp = Date.now();
    const randomId = Math.floor(Math.random() * 100000);
    gameChar._id = userId ? `char_${userId}_${timestamp}` : `dev_char_${timestamp}_${randomId}`;
    gameChar.id = gameChar._id;

    // Set starting position
    gameChar.position = { ...GAME_CONSTANTS.DEFAULTS.SPAWN_POS };

    return gameChar;
  }

  /**
   * Validate and convert quest data
   */
  private convertQuests(questData: any, completedQuestIds: string[] = []): QuestLog {
    const quests: QuestLog = {
      active: [],
      completed: [...completedQuestIds]
    };

    if (!questData) return quests;

    if (Array.isArray(questData.completed)) {
      quests.completed = questData.completed.filter((questId: unknown): questId is string => typeof questId === 'string');
    }

    // Handle Map format from Colyseus
    if (questData instanceof Map) {
      questData.forEach((progress: number, questId: string) => {
        quests.active.push({
          id: questId,
          name: questId, // Placeholder - should look up from quest config
          description: '',
          type: 'kill',
          targetId: 'unknown',
          targetCount: 5,
          currentCount: progress,
          completed: progress >= 5,
          rewards: { xp: 100, gold: 50 }
        });
      });
    } else if (typeof questData === 'object') {
      if (Array.isArray(questData.active)) {
        questData.active.forEach((quest: any) => {
          if (!quest?.id) return;
          quests.active.push({
            id: quest.id,
            name: quest.name || quest.id,
            description: quest.description || '',
            type: quest.type || 'kill',
            targetId: quest.targetId || '',
            targetCount: quest.targetCount || 0,
            currentCount: quest.currentCount || 0,
            completed: !!quest.completed,
            rewards: quest.rewards || { xp: 0, gold: 0 }
          });
        });
        return quests;
      }

      // Handle object format
      Object.entries(questData).forEach(([questId, progress]) => {
        if (questId === 'completed') return;
        quests.active.push({
          id: questId,
          name: questId,
          description: '',
          type: 'kill',
          targetId: 'unknown',
          targetCount: 5,
          currentCount: progress as number,
          completed: (progress as number) >= 5,
          rewards: { xp: 100, gold: 50 }
        });
      });
    }

    return quests;
  }

  private serializeQuests(quests: QuestLog): Record<string, number> {
    const questData: Record<string, number> = {};

    quests.active.forEach(quest => {
      questData[quest.id] = quest.currentCount;
    });

    return questData;
  }

  private validateNumber(value: any, defaultValue: number, min: number, max: number): number {
    const num = Number(value);
    if (isNaN(num)) return defaultValue;
    return Math.min(Math.max(num, min), max);
  }
}
